import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Address3HelperService, AddressFormGroup } from '@shared/components/address3/address3-helper.service';
import { SELECT_INVALID_SOLDO_FIELD, collectFormErrors, scrollToFirstResult } from '@shared/formHelpers';
import { Address, ApplicationSegment, Company, CountryMetadata } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService } from '@shared/services/country.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'trading-address',
  templateUrl: './trading-address.component.html',
  styleUrl: './trading-address.component.scss'
})
export class TradingAddressComponent implements OnInit {
    private readonly subStepName = "Company Trading Address";
    private companyToSave: Company;
    loading = false;
    onlyActiveMarket: boolean;
    form: FormGroup<{
        tradingAddressDifferent: FormControl<boolean>,
        tradingAddress?: FormGroup<AddressFormGroup>
    }>;
    tradingAddressFormGroup: UntypedFormGroup;
    registeredAddress: Address;
    translatedCountries: CountryMetadata[] = [];

    constructor(
        private readonly fb: FormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly address3Helper: Address3HelperService,
        private readonly countryService: CountryService,
        private readonly translate: TranslateService,
        private readonly loadingScreenService: LoadingScreenService,
        private readonly destroyRef: DestroyRef
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const company: Company = this.route.snapshot.data.company;
        this.companyToSave = RegistrationHelper.cloneCompany(company);
        this.onlyActiveMarket = this.registrationService.getSegment() === ApplicationSegment.selfServe;
        this.registeredAddress = company?.registeredAddress;

        this.translatedCountries = this.route.snapshot.data.countryList;
        this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(async () => {
            this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');
            this.translatedCountries = await this.countryService.getCurrentCountries();
            this.loadingScreenService.hide();
        });

        this.form = this.fb.group({
            tradingAddressDifferent: [Boolean(this.isTradingAddressDifferent(company))]
        });

        this.tradingAddressFormGroup = this.address3Helper.createPrepopulatedAddressFormGroup(company?.tradingAddress ?? {
            countryISO: company?.registeredAddress?.countryISO
        });

        if (this.isTradingAddressDifferent(company)) {
            this.addAddressForm();
        } else {
            this.removeAddressForm();
        }

        this.form.controls.tradingAddressDifferent.valueChanges.subscribe((value) => {
            if (value) {
                this.addAddressForm();
            } else {
                this.removeAddressForm();
            }
        });

    }

    private addAddressForm() {
        this.form.addControl("tradingAddress", this.tradingAddressFormGroup);
    }

    private removeAddressForm() {
        this.form.removeControl("tradingAddress");
    }

    private isValid(): boolean {
        if (this.form.value.tradingAddressDifferent) {
            this.form.markAllAsTouched();
        }
        return this.form.valid;
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.isValid()) {
            this.loading = true;
            const formValues = this.form.value;
            const tradingAddress = formValues.tradingAddressDifferent ? formValues.tradingAddress : null;

            if (tradingAddress) {
                this.companyToSave.tradingAddress = {
                    line1: tradingAddress.line1,
                    line2: tradingAddress.line2,
                    city: tradingAddress.city,
                    postalCode: tradingAddress.postalCode,
                    countryISO: tradingAddress.countryISO,
                    province: tradingAddress.province,
                    searchRefCode: tradingAddress.searchRefCode
                };
            } else {
                this.companyToSave.tradingAddress = null;
            }

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            try {
                await this.registrationService.saveCompany(this.companyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.TRADING_ADDRESS);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save company failed', e, 'with company', this.companyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD);
        }
    }

    private isTradingAddressDifferent(company: Company): boolean {
        return (
            company.tradingAddress?.city?.length > 0
            && Object.keys(company.registeredAddress).some(property => company.registeredAddress[property] !== company.tradingAddress[property])
        );
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }


}
